import { Auth0Client as Auth0ClientClass, Auth0ClientOptions } from '@auth0/auth0-spa-js';
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client.d';
import * as config from 'config';
import { PermissionsEnum } from 'consts/permissions';
import { LocalStoredAuthData } from './Auth.types';

let auth0Instance: Auth0Client;

const getAuth0Config = (): Auth0ClientOptions => ({
  audience: config.auth0.audience,
  client_id: config.auth0.clientID,
  domain: config.auth0.domain,
  redirect_uri: getRedirectUri(),
  scope: permissionScope(),
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
  useRefreshTokensFallback: false,
});

const clearAuth0Cache = (): void => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith('@@auth0'))
    .forEach((key) => localStorage.removeItem(key));
};

const init = async (): Promise<void> => {
  if (auth0Instance) return;

  const authConfig = getAuth0Config();
  auth0Instance = new Auth0ClientClass(authConfig);

  try {
    await auth0Instance.checkSession();
  } catch {
    clearAuth0Cache();
  }
};

const getData = (): LocalStoredAuthData => {
  const emptyResult = {
    userName: undefined,
    email: undefined,
    token: undefined,
    markets: [],
    permissions: [],
  };
  try {
    const auth0Key = Object.keys(localStorage).find((key) => key.includes('auth0')) || '';
    const data = JSON.parse(localStorage.getItem(auth0Key) || '{}');
    if (Object.keys(data).length) {
      return {
        token: data.body.access_token,
        userName: data.body.decodedToken.user.name,
        email: data.body.decodedToken.user.email,
        markets: data.body.decodedToken.user['https://auth.viooh.com/authentication/groups'] || [],
        permissions: data.body.decodedToken.user['https://auth.viooh.com/authentication/permissions'] || [],
      };
    }
    return emptyResult;
  } catch (error) {
    return emptyResult;
  }
};

const redirectToUniversalLoginPage = async (returnTo?: string): Promise<void> => {
  await auth0Instance.loginWithRedirect({
    appState: returnTo ? { returnTo } : undefined,
  });
};

const isAuthenticated = async (): Promise<boolean> => {
  return auth0Instance.isAuthenticated();
};

let isRedirectingToLogin = false;

const getToken = async (): Promise<string> => {
  try {
    return await auth0Instance.getTokenSilently();
  } catch {
    if (!isRedirectingToLogin) {
      isRedirectingToLogin = true;
      await auth0Instance.loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
      });
    }
    return '';
  }
};

const hasPermission = (permission: string): boolean => {
  const { permissions } = getData();
  return permissions.includes(permission);
};

const authenticate = async (): Promise<{ returnTo?: string }> => {
  const result = await auth0Instance.handleRedirectCallback();
  return { returnTo: result?.appState?.returnTo };
};

const logout = (): void => {
  auth0Instance.logout({
    returnTo: getLogoutReturnUri(),
  });
};

export default {
  init,
  redirectToUniversalLoginPage,
  isAuthenticated,
  hasPermission,
  authenticate,
  logout,
  getData,
  getToken,
};

/**
 *
 * Private functions
 *
 */

function permissionScope(): string {
  const permissions = Object.values(PermissionsEnum).join(' ');

  return `${config.auth0.scope} ${permissions}`;
}

function getRedirectUri(): string {
  const url = new URL(`${config.app.protocol}://${config.app.host}`);
  if (config.app.port) {
    url.port = config.app.port;
  }
  url.pathname = `auth-callback/`;
  return url.toString();
}

function getLogoutReturnUri(): string {
  const url = new URL(`${config.app.protocol}://${config.app.host}`);
  if (config.app.port) {
    url.port = config.app.port;
  }
  return url.toString();
}
